import type {
  Cell,
  Column,
  FieldMap,
  Option,
  ProjectedRow,
  Tone,
  ViewBinding,
} from './contract.js';

/**
 * LA PROJECTION : enregistrement metier -> `ProjectedRow`.
 *
 * C'est l'etape qui fait tomber le generique. En entree, des `T` que seule
 * l'app connait, plus le binding qu'elle a declare ; en sortie, des lignes
 * neutres, forme UNIQUE que les cinq vues du core (et toute vue custom)
 * savent lire. Aucune vue ne voit jamais un `T`.
 *
 * Regles tenues ici :
 * - un role absent du binding ne produit rien (pas d'erreur, pas de valeur
 *   fabriquee) ;
 * - une valeur vide affiche le texte `empty` du champ, pas une chaine vide ;
 * - une valeur hors catalogue n'est JAMAIS jetee : elle devient une option
 *   neutre a son nom, sinon une ligne disparaitrait de l'ecran ;
 * - un role est editable SSI l'ecran a fourni `set()` dessus ; une COLONNE
 *   pareil, mais son editabilite vit dans sa cellule (`cells[key].editable`),
 *   jamais dans `row.editable`, reserve aux roles ;
 * - la pastille "Nouveau" (`isNew`) est POSEE ici, jamais devinee par une vue :
 *   c'est un fait sur la ligne, au meme titre que son titre.
 */

/** Les roles connus du core : tout le reste part dans `ProjectedRow.extra`. */
const KNOWN_ROLES = new Set<string>([
  'title',
  'subtitle',
  'avatar',
  'status',
  'badges',
  'groupKey',
  'lane',
  'dateStart',
  'dateEnd',
]);

/** Libelle de la colonne fourre-tout quand la valeur d'un role catalogue est vide. */
export const OTHER_LABEL = 'Autres';

/** Le ton d'une valeur qui n'est pas dans le catalogue du tenant. */
const OUT_OF_CATALOG_TONE: Tone = 'neutral';

/**
 * Les roles d'un binding, lus SANS le mensonge de type de l'intersection
 * `Partial<Record<FieldRole, ...>> & Record<string, ...>` (qui fait croire que
 * `roles.title` existe toujours). Un seul endroit ou la verite est retablie.
 */
function rolesOf<T>(binding: ViewBinding<T>): Record<string, FieldMap<T> | undefined> {
  return binding.roles as Record<string, FieldMap<T> | undefined>;
}

/** Une valeur est-elle vide (rien a afficher) ? */
function isBlank(value: Cell | undefined): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Met une valeur brute en texte. Les dates suivent la locale du poste (c'est
 * un souci d'affichage, jamais de stockage) ; les booleens parlent francais.
 */
export function cellToText(value: Cell | undefined, empty = ''): string {
  if (isBlank(value)) return empty;
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  return String(value);
}

/**
 * Deux valeurs sont-elles la MEME ? La regle de l'ECRITURE, pas celle du tri :
 * elle decide si refermer un editeur doit ecrire (et cocher) ou ne rien faire du
 * tout. Une fausse coche sur une valeur inchangee serait un mensonge, et la
 * meme regle doit valoir cote vue (qui referme) et cote core (qui ecrit), sinon
 * l'un des deux coche sans que l'autre ecrive.
 *
 * Le vide sous toutes ses formes ('', null, undefined) est une seule et meme
 * absence de valeur ; deux dates se comparent a l'instant, jamais par identite.
 */
export function sameCell(a: Cell | undefined, b: Cell | undefined): boolean {
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank || bBlank) return aBlank && bBlank;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  return a === b;
}

/**
 * Compare deux valeurs pour le tri. Les vides finissent TOUJOURS en bas, quel
 * que soit le sens : une ligne sans echeance ne remonte pas en tete parce
 * qu'on a clique deux fois.
 */
export function compareCells(a: Cell | undefined, b: Cell | undefined): number {
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank && bBlank) return 0;
  if (aBlank) return 1;
  if (bBlank) return -1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Resout une valeur dans le catalogue d'un role (statut, colonne de kanban,
 * couloir). Trois cas, aucun ne perd la ligne :
 * - valeur vide : l'option fourre-tout ("Autres", ou le texte `empty` du champ) ;
 * - valeur au catalogue : l'option du tenant, avec son libelle et son ton ;
 * - valeur hors catalogue : une option neutre a son nom.
 */
export function resolveOption<T>(value: Cell | undefined, field: FieldMap<T> | undefined): Option {
  if (isBlank(value)) {
    return { value: '', label: field?.empty ?? OTHER_LABEL, tone: OUT_OF_CATALOG_TONE };
  }
  const raw = value instanceof Date ? value.toISOString() : String(value);
  const catalog = field?.options?.() ?? [];
  const found = catalog.find((option) => option.value === raw);
  if (found) return found;
  return { value: raw, label: cellToText(value), tone: OUT_OF_CATALOG_TONE };
}

/** Toutes les valeurs d'un champ : `getMany` s'il existe, sinon `get` seul. */
function valuesOf<T>(record: T, field: FieldMap<T>): Cell[] {
  if (field.getMany) return field.getMany(record);
  return [field.get(record)];
}

/** Met une valeur en Date quand c'en est une, sinon rien (pas de date inventee). */
function toDate(value: Cell | undefined): Date | undefined {
  if (value instanceof Date) return value;
  if (typeof value === 'string' && value !== '') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
  return undefined;
}

/* ------------------------------------------------------------------ */
/* La pastille "Nouveau" : le CALCUL, jamais le rendu                  */
/* ------------------------------------------------------------------ */

/**
 * CE QUE LA PROJECTION DOIT SAVOIR pour allumer `isNew`, tel que `useScreen`
 * le lui donne (la memoire locale au poste vit dans `newRecords.ts`).
 *
 * ABSENT = personne ne demande la pastille, et `isNew` retombe sur `false`.
 * C'est ce qui garde `projectRecord` et `projectRecords` retro-compatibles
 * pour leurs appelants d'avant ce chantier (la fiche, qui ne porte jamais de
 * pastille : l'ouvrir est justement ce qui l'eteint).
 */
export interface NewRecordsInput {
  /** Date de reference de la porte, en millisecondes. Rien d'anterieur n'est neuf. */
  since: number;
  /** Les ids deja ouverts sur ce poste depuis cette date. */
  seen: ReadonlySet<string>;
}

/**
 * `createdAt` d'un enregistrement quelconque, en millisecondes.
 *
 * C'est un CHAMP STANDARD pose par le SDK sur les 57 familles
 * (packages/sdk/src/standard-fields.ts) : on le lit donc par son NOM, jamais
 * par un role de binding. Aucun ecran n'a a declarer quoi que ce soit pour que
 * la pastille marche, et aucun n'a le pouvoir de la fausser en projetant une
 * autre date sur un role.
 *
 * Lecture DEFENSIVE, comme `isArchived` cote `useScreen` : le core ne connait
 * pas `T`, et un chargeur de decor (ui-lab) n'a pas de `createdAt` du tout.
 * Sans date lisible, la ligne n'est JAMAIS neuve : mieux vaut ne rien signaler
 * que signaler au hasard.
 */
function createdAtOf(record: unknown): number | null {
  const value = (record as { createdAt?: unknown } | null)?.createdAt;
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : time;
  }
  // Le SDK rend des `Date`. Une chaine ISO ou un nombre arrivent d'un chargeur
  // qui n'est pas passe par lui (decor en dur, cache reserialise).
  if (typeof value === 'string' || typeof value === 'number') {
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
  }
  return null;
}

/**
 * UNE FICHE EST RECENTE si elle a ete creee APRES la date de reference de la
 * porte. UNE SEULE condition : ce que l'utilisateur a ouvert reste recent.
 *
 * Elle ne sort PLUS sur la ligne projetee : depuis le retrait du repli du
 * lisere (07/08/2026, voir core/NewBadge.tsx), son seul role est de servir de
 * premiere condition a `isNew`. Elle reste une fonction a part parce qu'elle
 * repond a une question differente ("creee apres la reference") de celle que
 * pose `isNew` ("creee apres la reference ET jamais ouverte").
 *
 * Comparaison STRICTE : la date de reference est posee a la premiere visite,
 * donc ce qui existait a cet instant precis n'est pas une nouveaute.
 */
export function isRecordRecent(record: unknown, input?: NewRecordsInput): boolean {
  if (input == null) return false;
  const createdAt = createdAtOf(record);
  return createdAt != null && createdAt > input.since;
}

/**
 * UNE FICHE EST NOUVELLE si elle est RECENTE ET que son id n'est pas dans
 * l'ensemble des fiches deja ouvertes.
 *
 * Les deux conditions comptent, et aucune ne suffit : la premiere empeche la
 * toute premiere visite d'allumer les 500 lignes deja en base, la seconde
 * eteint ce que l'utilisateur a ouvert. C'est cette quantite-la, et elle seule,
 * que les vues RENDENT (lisere et pastille).
 */
export function isRecordNew(record: unknown, id: string, input?: NewRecordsInput): boolean {
  if (input == null) return false;
  if (input.seen.has(id)) return false;
  return isRecordRecent(record, input);
}

/** Projette UN enregistrement en ligne neutre, selon le binding de la vue active. */
export function projectRecord<T>(
  record: T,
  id: string,
  binding: ViewBinding<T> | undefined,
  /**
   * Facultatif, et faux par defaut : les appelants d'avant ce chantier
   * (fiche, vue custom qui reprojette) gardent exactement leur comportement.
   */
  isNew = false,
): ProjectedRow {
  const roles = binding ? rolesOf(binding) : {};
  const columns: Column<T>[] = binding?.columns ?? [];

  const titleField = roles['title'];
  const subtitleField = roles['subtitle'];
  const avatarField = roles['avatar'];
  const statusField = roles['status'];
  const badgesField = roles['badges'];
  const groupField = roles['groupKey'];
  const laneField = roles['lane'];
  const startField = roles['dateStart'];
  const endField = roles['dateEnd'];

  // Le titre ne peut pas etre vide : une ligne sans identite est illisible.
  const title = titleField ? cellToText(titleField.get(record), titleField.empty ?? '') : '';

  const row: ProjectedRow = {
    id,
    title: title === '' ? id : title,
    badges: [],
    groups: [],
    lanes: [],
    extra: {},
    cells: {},
    editable: {},
    isNew,
  };

  if (subtitleField) {
    row.subtitle = cellToText(subtitleField.get(record), subtitleField.empty ?? '');
  }
  if (avatarField) {
    const value = avatarField.get(record);
    if (!isBlank(value)) row.avatar = cellToText(value);
  }
  if (statusField) {
    row.status = resolveOption(statusField.get(record), statusField);
  }
  if (badgesField) {
    // Contrairement aux couloirs, une ligne SANS etiquette est le cas normal :
    // les valeurs vides sont ecartees, jamais resolues en "Autres".
    row.badges = valuesOf(record, badgesField)
      .filter((value) => !isBlank(value))
      .map((value) => resolveOption(value, badgesField));
  }
  if (groupField) {
    row.groups = valuesOf(record, groupField).map((value) => resolveOption(value, groupField));
  }
  if (laneField) {
    row.lanes = valuesOf(record, laneField).map((value) => resolveOption(value, laneField));
  }
  if (startField) {
    const start = toDate(startField.get(record));
    if (start) row.start = start;
  }
  if (endField) {
    const end = toDate(endField.get(record));
    if (end) row.end = end;
  }

  // Roles libres (vue custom) : la valeur brute, sans interpretation.
  for (const [name, field] of Object.entries(roles)) {
    if (field == null) continue;
    row.editable[name] = typeof field.set === 'function';
    if (KNOWN_ROLES.has(name)) continue;
    row.extra[name] = field.get(record);
  }

  // Colonnes : la valeur comparable, son rendu, et son editabilite, cote a
  // cote. C'est le SEUL endroit du repo qui construit une cellule.
  for (const column of columns) {
    const value = column.value(record);
    row.cells[column.key] = {
      value,
      node: column.render ? column.render(record) : cellToText(value),
      // Une colonne sans `set()` reste en lecture seule : la fiche ne pose
      // alors aucune affordance, pas meme au survol.
      editable: typeof column.set === 'function',
    };
  }

  return row;
}

/**
 * Projette un jeu d'enregistrements. L'ordre d'entree est conserve.
 *
 * `newRecords` est le SEUL endroit du produit ou la pastille "Nouveau" se
 * calcule : une vue la lit sur la ligne (`row.isNew`), elle ne la deduit
 * jamais elle-meme. Omis (ou reperage eteint dans les reglages) : toutes les
 * lignes sortent avec `isNew: false`, et `createdAt` n'est meme pas lu.
 *
 * UN SEUL booleen sort d'ici, `isNew` (creee apres la date de reference ET
 * jamais ouverte). La date n'est lue qu'une fois par ligne.
 */
export function projectRecords<T>(
  records: readonly T[],
  getId: (record: T) => string,
  binding: ViewBinding<T> | undefined,
  newRecords?: NewRecordsInput,
): ProjectedRow[] {
  return records.map((record) => {
    const id = getId(record);
    /* `isRecordRecent` sert encore, mais SEULEMENT a calculer `isNew` : la
       ligne projetee ne porte plus de booleen "recent". Le repli qui s'en
       servait a ete retire (voir core/NewBadge.tsx). */
    const isNew =
      isRecordRecent(record, newRecords) && newRecords != null && !newRecords.seen.has(id);
    return projectRecord(record, id, binding, isNew);
  });
}

/**
 * Applique un patch OPTIMISTE sur une ligne deja projetee : la vue voit le
 * changement tout de suite, le core remet en place si l'ecriture echoue.
 * Retourne une NOUVELLE ligne (les vues comparent par identite).
 */
export function applyRolePatch<T>(
  row: ProjectedRow,
  role: string,
  value: Cell,
  field: FieldMap<T> | undefined,
): ProjectedRow {
  const patched: ProjectedRow = { ...row };
  switch (role) {
    case 'title':
      patched.title = cellToText(value, field?.empty ?? '') || row.id;
      break;
    case 'subtitle':
      patched.subtitle = cellToText(value, field?.empty ?? '');
      break;
    case 'avatar':
      patched.avatar = isBlank(value) ? undefined : cellToText(value);
      break;
    case 'status':
      patched.status = resolveOption(value, field);
      break;
    case 'groupKey':
      patched.groups = [resolveOption(value, field)];
      break;
    case 'lane':
      patched.lanes = [resolveOption(value, field)];
      break;
    case 'dateStart':
      patched.start = toDate(value);
      break;
    case 'dateEnd':
      patched.end = toDate(value);
      break;
    default:
      patched.extra = { ...row.extra, [role]: value };
      break;
  }
  return patched;
}

/**
 * Le jumeau d'`applyRolePatch` pour une COLONNE. Meme role, meme contrat de
 * retour (une nouvelle ligne, les vues comparent par identite), deux details
 * qui ne se devinent pas :
 *
 * 1. LA COPIE EST PROFONDE JUSQU'A LA CELLULE. `applyRolePatch` fait un
 *    `{ ...row }` plat : le sac `cells` y reste PARTAGE avec la ligne d'origine.
 *    Ecrire dedans muterait une ligne que d'autres resultats de memo tiennent
 *    encore. On clone donc `cells` PUIS la cellule visee.
 * 2. LE NODE OPTIMISTE EST BRUT (`cellToText`), jamais `column.render(record)` :
 *    le record en memoire porte encore l'ANCIENNE valeur, rappeler `render()`
 *    reafficherait l'ancien texte formate. L'habillage de l'app ("290 EUR", une
 *    date en francais) revient au `reload()` qui suit l'ecriture reussie. Ne pas
 *    "corriger" en rappelant `render()`.
 *
 * Une cle inconnue de `cells` ne fabrique RIEN : la colonne n'est pas declaree
 * dans le binding de la vue courante, il n'y a nulle part ou l'afficher.
 */
export function applyColumnPatch(
  row: ProjectedRow,
  key: string,
  value: Cell,
  options?: Option[],
): ProjectedRow {
  const cell = row.cells[key];
  if (cell == null) return row;
  /*
   * Une colonne a CATALOGUE affiche un libelle, pas la valeur stockee. Sans
   * cette resolution, choisir "Optique" montrait `optique` le temps de
   * l'aller-retour serveur, puis `Optique` au rechargement : un clignotement a
   * chaque choix, exactement la ou l'utilisateur regarde.
   *
   * `render()` ne peut pas servir ici : il prend le RECORD, qui porte encore
   * l'ancienne valeur tant que le serveur n'a pas repondu. Le catalogue, lui,
   * traduit la valeur seule.
   */
  const label = options?.find((option) => option.value === String(value ?? ''))?.label;
  return {
    ...row,
    cells: { ...row.cells, [key]: { ...cell, value, node: label ?? cellToText(value) } },
  };
}

/**
 * Recherche COTE CLIENT (lot 1) : le texte est confronte a ce que la ligne
 * MONTRE (titre, sous-titre, statut, valeurs de colonnes). Chercher sur ce qui
 * est affiche est la seule regle qu'un utilisateur peut deviner.
 */
export function matchesSearch(row: ProjectedRow, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (needle === '') return true;
  const haystack: string[] = [row.title];
  if (row.subtitle != null) haystack.push(row.subtitle);
  if (row.status != null) haystack.push(row.status.label);
  for (const group of row.groups) haystack.push(group.label);
  for (const lane of row.lanes) haystack.push(lane.label);
  for (const cell of Object.values(row.cells)) haystack.push(cellToText(cell.value));
  return haystack.some((text) => text.toLowerCase().includes(needle));
}

/**
 * La valeur comparable d'une ligne pour une cle de tri : une colonne d'abord,
 * sinon un role connu. Sans correspondance, la ligne ne bouge pas.
 */
export function sortValueOf(row: ProjectedRow, key: string): Cell | undefined {
  const cell = row.cells[key];
  if (cell) return cell.value;
  switch (key) {
    case 'title':
      return row.title;
    case 'subtitle':
      return row.subtitle ?? null;
    case 'status':
      return row.status?.label ?? null;
    case 'dateStart':
      return row.start ?? null;
    case 'dateEnd':
      return row.end ?? null;
    default:
      return row.extra[key];
  }
}

/** Les cles que `sortValueOf` resout SANS cellule : les roles de premiere classe. */
const SORT_ROLE_KEYS = new Set<string>(['title', 'subtitle', 'status', 'dateStart', 'dateEnd']);

/**
 * La vue ACTIVE sait-elle resoudre cette cle de tri toute seule ? Oui quand la
 * cle est un role de premiere classe (`sortValueOf` la lit sur la ligne), une
 * colonne du binding actif (la ligne porte sa cellule) ou un role libre (la
 * ligne porte sa valeur dans `extra`). Sinon, la cle appartient a un AUTRE
 * binding de la porte (le tri est a l'ECRAN, pas a la vue) et le core doit
 * fournir un acces de repli : voir le memo `projected` de useScreen.
 */
export function resolvesSortKey<T>(binding: ViewBinding<T> | undefined, key: string): boolean {
  if (SORT_ROLE_KEYS.has(key)) return true;
  if (binding == null) return false;
  if (binding.columns?.some((column) => column.key === key)) return true;
  const field = rolesOf(binding)[key];
  return field != null && !KNOWN_ROLES.has(key);
}

/* ------------------------------------------------------------------ */
/* L'avatar : resolu UNE fois, pour toutes les vues                     */
/* ------------------------------------------------------------------ */

/**
 * Le role `avatar` porte "une URL ou des initiales, selon ce que l'ecran
 * fournit" (contrat). On tranche sur la FORME de la chaine, jamais sur une
 * convention cachee : tout ce qui ressemble a une ressource est charge comme
 * une image (http(s), data:, blob:, chemin absolu).
 */
const IMAGE_SOURCE = /^(?:https?:\/\/|data:|blob:|\/)/i;

export function isImageSource(value: string): boolean {
  return IMAGE_SOURCE.test(value);
}

/**
 * Initiales : deux lettres au maximum, sinon le cercle deborde. Plusieurs mots
 * donnent leurs premieres lettres ("Jean Dupont" -> "JD"), un mot seul ses deux
 * premieres ("Minou" -> "MI"), une valeur deja courte reste telle quelle
 * ("AB" -> "AB").
 */
export function initialsOf(text: string): string {
  const words = text
    .trim()
    .split(/\s+/)
    .filter((word) => word !== '');
  const [first, second] = words;
  if (first == null) return '';
  if (second != null) return (first.charAt(0) + second.charAt(0)).toUpperCase();
  return first.slice(0, 2).toUpperCase();
}

/** Une pastille de tete de ligne, resolue : soit une image, soit des initiales. */
export interface AvatarSlot {
  /** L'image a charger. Absente quand la pastille tombe sur les initiales. */
  src?: string;
  /** Les initiales a centrer dans le cercle (jamais la chaine brute). */
  initials: string;
}

/**
 * LA PASTILLE D'UNE LIGNE, resolue UNE SEULE FOIS pour toutes les vues.
 *
 * Chaque vue decidait avant si le role `avatar` etait une image ou des
 * initiales, avec sa propre regle : trois tests differents et deux facons de
 * fabriquer les initiales, donc la meme donnee rendait trois pastilles selon la
 * vue. La regle vit ici, et nulle part ailleurs :
 * - valeur qui ressemble a une ressource : c'est l'image ;
 * - toute autre valeur : ses initiales ;
 * - valeur absente sur CETTE ligne : les initiales du titre, jamais un rond
 *   vide au milieu d'une colonne de visages.
 *
 * Si l'ecran n'a pas declare le role du tout, la vue ne dessine simplement pas
 * de pastille : c'est `hasAvatarRole` qui tranche, au niveau du JEU, jamais
 * ligne par ligne (sinon la colonne des titres danse d'une ligne a l'autre).
 */
export function avatarOf(row: ProjectedRow): AvatarSlot {
  const value = row.avatar;
  if (value != null && value !== '' && isImageSource(value)) {
    return { src: value, initials: initialsOf(row.title) };
  }
  return { initials: initialsOf(value != null && value !== '' ? value : row.title) };
}

/**
 * L'ecran a-t-il projete le role `avatar` sur ce jeu ? La question se pose au
 * niveau de la LISTE (hauteur de ligne fixe, titres alignes), pas de la ligne.
 */
export function hasAvatarRole(rows: readonly ProjectedRow[]): boolean {
  return rows.some((row) => row.avatar != null);
}

/**
 * Le catalogue COMPLET d'un role, tel que la vue le recoit :
 * celui du tenant, PUIS les valeurs hors catalogue effectivement presentes.
 *
 * C'est la garantie "un statut hors catalogue ne fait jamais disparaitre une
 * ligne" : au lieu d'un seul fourre-tout "Autres", chaque valeur inconnue
 * garde sa colonne, en ton neutre, apres le catalogue. L'option fourre-tout
 * ("Autres") n'apparait que si des lignes n'ont AUCUNE valeur pour ce role.
 */
export function catalogOf<T>(
  role: string,
  field: FieldMap<T> | undefined,
  rows: readonly ProjectedRow[],
): Option[] {
  const catalog = field?.options?.() ?? [];
  const known = new Set(catalog.map((option) => option.value));
  const extras: Option[] = [];
  const seen = new Set<string>();

  const collect = (option: Option): void => {
    if (known.has(option.value) || seen.has(option.value)) return;
    seen.add(option.value);
    extras.push(option);
  };

  for (const row of rows) {
    if (role === 'status' && row.status) collect(row.status);
    else if (role === 'groupKey') for (const group of row.groups) collect(group);
    else if (role === 'lane') for (const lane of row.lanes) collect(lane);
  }

  return [...catalog, ...extras];
}
